import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { UnreadProvider } from './contexts/UnreadContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { BrandingProvider } from './contexts/BrandingContext';
import Layout from './components/Layout';
import MobileAppBanner from './components/MobileAppBanner';
import Login from './pages/Login';
import Register from './pages/Register';
import LandingPage from './pages/LandingPage';
import RequestDemo from './pages/RequestDemo';
import Dashboard from './pages/Dashboard';
import TeamChat from './pages/TeamChat';
import Events from './pages/Events';
import Budget from './pages/Budget';
import Payments from './pages/Payments';
import PaymentManagement from './pages/PaymentManagement';
import PaymentCallback from './pages/PaymentCallback';
import StripeConnectCallback from './pages/StripeConnectCallback';
import Videos from './pages/Videos';
import TeamRoster from './pages/TeamRoster';
import Uniforms from './pages/Uniforms';
import Profile from './pages/Profile';
import TeamManagement from './pages/TeamManagement';
import OrganizationManagement from './pages/OrganizationManagement';
import VideoAnalysis from './pages/VideoAnalysis';
import UserManagement from './pages/UserManagement';
import AppManagement from './pages/AppManagement';
import Invoicing from './pages/Invoicing';
import StripeSettings from './pages/StripeSettings';
import TestTeamSelector from './pages/TestTeamSelector';
import JoinTeam from './pages/JoinTeam';
import ParentDashboardPage from './pages/ParentDashboard';
import TryoutAssignment from './pages/TryoutAssignment';
import ProgramAssignment from './pages/ProgramAssignment';
import Programs from './pages/Programs';
import Facilities from './pages/Facilities';
import FacilityDetail from './pages/FacilityDetail';
import ScheduleGenerator from './pages/ScheduleGenerator';
import Contacts from './pages/Contacts';
import TryoutScoring from './pages/TryoutScoring';
import PublicEvent from './pages/PublicEvent';
import PublicCheckout from './pages/PublicCheckout';
import PublicOrganization from './pages/PublicOrganization';
import PublicThanks from './pages/PublicThanks';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Support from './pages/Support';
import SmsOptIn from './pages/SmsOptIn';
import SmsCompliance from './pages/SmsCompliance';
import LeagueReports from './pages/LeagueReports';
import Subscriptions from './pages/Subscriptions';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import ActivateAccount from './pages/ActivateAccount';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import * as serviceWorker from './utils/serviceWorker';
import { getPublicOrganizationSlug } from './utils/publicOrganizationHost';

const queryClient = new QueryClient();

// Protected Route Component - redirects unauthenticated users to login
const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true, state: { from: location } });
    }
  }, [token, navigate, location]);

  if (!token) {
    // Brief loading state while redirecting to /login
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse text-gray-500">Loading...</div>
      </div>
    );
  }

  return children;
};

// The native iOS/Android apps load this same web app (see capacitor.config.json's
// server.url), so "/" has to branch at runtime: web visitors see the marketing
// landing page, native app users skip straight past it like before.
const RootRoute = () => {
  if (Capacitor.isNativePlatform()) {
    const token = localStorage.getItem('token');
    return <Navigate to={token ? '/dashboard' : '/login'} replace />;
  }
  if (getPublicOrganizationSlug()) {
    return <PublicOrganization />;
  }
  return <LandingPage />;
};

// When a Universal Link / App Link (e.g. an invoice link in an email) opens the
// native app, route the webview to that page instead of leaving it where it was.
const DeepLinkHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    const listener = CapacitorApp.addListener('appUrlOpen', ({ url }) => {
      try {
        const { pathname, search, hash } = new URL(url);
        navigate(`${pathname}${search}${hash}`);
      } catch (error) {
        console.error('Failed to handle app URL:', url, error);
      }
    });
    return () => {
      listener.then((handle) => handle.remove());
    };
  }, [navigate]);

  return null;
};

function App() {
  useEffect(() => {
    // Register service worker
    serviceWorker.register({
      onSuccess: () => {
        console.log('Service worker registered successfully');
      },
      onUpdate: () => {
        console.log('New content available, please refresh');
      }
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
        <BrandingProvider>
        <UnreadProvider>
          <SocketProvider>
            <div className="App">
              <Routes>
                {/* Public routes (no auth) */}
                <Route path="/public/event" element={<PublicEvent />} />
                <Route path="/public/checkout" element={<PublicCheckout />} />
                <Route path="/public/thanks" element={<PublicThanks />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/support" element={<Support />} />
                <Route path="/sms-opt-in" element={<SmsOptIn />} />
                <Route path="/sms-compliance" element={<SmsCompliance />} />
                <Route path="/" element={<RootRoute />} />
                <Route path="/request-demo" element={<RequestDemo />} />

                {/* Auth routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/activate" element={<ActivateAccount />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/join-team" element={<JoinTeam />} />
                <Route
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="chat" element={<TeamChat />} />
                  <Route path="events" element={<Events />} />
                  <Route path="budget" element={<Navigate to="/payments?tab=budget" replace />} />
                  <Route path="payments" element={<Payments />} />
                  {/* Invoice emails link to /invoices/:id — invoices live on the Payments page */}
                  <Route path="invoices" element={<Navigate to="/payments" replace />} />
                  <Route path="invoices/:invoiceId" element={<Navigate to="/payments" replace />} />
                  <Route path="payment-management" element={<PaymentManagement />} />
                  <Route path="payment/callback/paypal" element={<PaymentCallback />} />
                  <Route path="payment/callback/stripe-connect" element={<StripeConnectCallback />} />
                  <Route path="videos" element={<Videos />} />
                  <Route path="roster" element={<TeamRoster />} />
                  <Route path="uniforms" element={<Uniforms />} />
                  <Route path="profile" element={<Profile />} />
                  <Route path="team-management" element={<TeamManagement />} />
                  <Route path="organization-management" element={<OrganizationManagement />} />
                  <Route path="video-analysis" element={<VideoAnalysis />} />
                  <Route path="user-management" element={<UserManagement />} />
                  <Route path="app-management" element={<AppManagement />} />
                  <Route path="invoicing" element={<Invoicing />} />
                  <Route path="stripe-settings" element={<StripeSettings />} />
                  <Route path="tryout-assignment" element={<TryoutAssignment />} />
                  <Route path="program-assignment" element={<ProgramAssignment />} />
                  <Route path="programs" element={<Programs />} />
                  <Route path="facilities" element={<Facilities />} />
                  <Route path="facility/:facilityId" element={<FacilityDetail />} />
                  <Route path="scheduling" element={<ScheduleGenerator />} />
                  <Route path="contacts" element={<Contacts />} />
                  <Route path="tryout-scoring" element={<TryoutScoring />} />
                  <Route path="league-reports" element={<LeagueReports />} />
                  <Route path="subscription" element={<Navigate to="/payments?tab=subscription" replace />} />
                  <Route path="subscription/success" element={<SubscriptionSuccess />} />
                  {/* Public routes must not be nested here */}
                  <Route path="test-team-selector" element={<TestTeamSelector />} />
                  <Route path="parent-dashboard" element={<ParentDashboardPage />} />
                </Route>
              </Routes>
              <Toaster position="top-right" />
              <MobileAppBanner />
              <DeepLinkHandler />
            </div>
          </SocketProvider>
        </UnreadProvider>
        </BrandingProvider>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
